import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../components/ToastContainer';
import { t } from '../../utils/i18n';

export default function AcceptFriendInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const { showToast } = useToast();

  useEffect(() => {
    const acceptInvitation = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        showToast(t('friends.invalidInvitation') || 'Nevažeća pozivnica', 'error');
        setTimeout(() => {
          navigate('/dashboard/friends', { replace: true });
        }, 2000);
        return;
      }

      try {
        setStatus('processing');
        const response = await api.post('/friends/accept', { token });
        
        if (response?.data?.success) {
          setStatus('success');
          showToast(t('friends.invitationAccepted') || 'Pozivnica je uspešno prihvaćena!', 'success');
          setTimeout(() => {
            navigate('/dashboard/friends', { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          showToast(response?.data?.message || t('friends.acceptError') || 'Greška pri prihvatanju pozivnice', 'error');
          setTimeout(() => {
            navigate('/dashboard/friends', { replace: true });
          }, 3000);
        }
      } catch (error) {
        console.error('Error accepting invitation:', error);
        setStatus('error');
        const errorMessage = error.response?.data?.message || 
                           t('friends.acceptError') || 
                           'Greška pri prihvatanju pozivnice';
        showToast(errorMessage, 'error');
        setTimeout(() => {
          navigate('/dashboard/friends', { replace: true });
        }, 3000);
      }
    };

    acceptInvitation();
  }, [searchParams, navigate, showToast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('friends.processingInvitation') || 'Obrađivanje pozivnice...'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('friends.pleaseWait') || 'Molimo sačekajte...'}
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="mb-4">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('friends.invitationAccepted') || 'Pozivnica prihvaćena!'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('friends.nowConnected') || 'Sada ste povezani sa prijateljem.'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('friends.redirecting') || 'Preusmeravanje...'}
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="mb-4">
              <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('friends.acceptError') || 'Greška pri prihvatanju pozivnice'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('friends.invalidOrExpired') || 'Pozivnica je nevažeća ili je istekla.'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('friends.redirecting') || 'Preusmeravanje...'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

